import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Card,
  InputAdornment,
  Skeleton,
  Checkbox,
  Menu,
  Paper
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Person,
  FilterList,
  Refresh,
  Add,
  LockReset,
  Sort,
  TrendingUp,
  Wc
} from '@mui/icons-material';
import axios from 'axios';
import { GenderPredictor } from '../utils/genderPredictor';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  username: string;
  email: string;
  phoneNumber?: string;
  age: number;
  gender: string;
  bio?: string;
  location?: string;
  profilePicture?: string;
  isActive: boolean;
  isPremium: boolean;
  isBanned: boolean;
  banReason?: string;
  // Coffee Beans & Game Stats
  coffeeBeansPoints?: number;
  totalCoffeeBeansCollected?: number;
  beans?: number;
  totalBeans?: number;
  hintCount?: number;
  gameStats?: any;
  // Dates
  createdAt: Date;
  lastLoginAt?: Date;
  premiumExpiresAt?: Date;
  cafeFilterSettings?: any;
  filterSettings?: any;
  swipeFilterSettings?: any;
  totalMatches?: number;
  totalSwipes?: number;
  totalReports?: number;
}

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(300); // 300'den başlasın
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [premiumFilter, setPremiumFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [todayRegistrations, setTodayRegistrations] = useState(0); // Bugünkü kayıt sayısı
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // Sıralama durumu (en yeni önce)
  const [totalUsers, setTotalUsers] = useState(0); // Backend'den gelen toplam kullanıcı sayısı
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phoneNumber: '',
    age: 0,
    gender: '',
    bio: '',
    location: '',
    isActive: false,
    isPremium: false,
    isBanned: false,
    banReason: '',
    // Coffee Beans & Game Stats
    coffeeBeansPoints: 0,
    totalCoffeeBeansCollected: 0,
    beans: 0,
    totalBeans: 0,
    hintCount: 0
  });
  const [resetPasswordForm, setResetPasswordForm] = useState({
    sendEmail: true,
    newPassword: ''
  });
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [profilePictureBase64, setProfilePictureBase64] = useState<string>('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [checkingPremium, setCheckingPremium] = useState(false);
  const [premiumCheckResults, setPremiumCheckResults] = useState<any>(null);
  const [showPremiumResultsDialog, setShowPremiumResultsDialog] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkActionAnchorEl, setBulkActionAnchorEl] = useState<null | HTMLElement>(null);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Auto Prediction State
  const [showPredictionDialog, setShowPredictionDialog] = useState(false);
  const [predictionResults, setPredictionResults] = useState<any[]>([]);
  const [savingPredictions, setSavingPredictions] = useState(false);


  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // Kullanıcıları çek
      let url = `https://api.sobcof.com/api/users?page=${page + 1}&limit=${rowsPerPage}&status=${statusFilter}&premium=${premiumFilter}&gender=${genderFilter}&sortBy=createdAt&sortOrder=${sortOrder}`;

      // Search parametresini ekle
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.data);
        // Backend'den gelen toplam kullanıcı sayısını kaydet
        if (response.data.pagination?.totalUsers !== undefined) {
          setTotalUsers(response.data.pagination.totalUsers);
        }
      }

      // Bugünkü kayıt sayısını çek
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString();

      const todayResponse = await axios.get(`https://api.sobcof.com/api/users/stats/today?date=${todayISO}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (todayResponse.data.success) {
        setTodayRegistrations(todayResponse.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, premiumFilter, genderFilter, sortOrder, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Tarih sıralamasını değiştir
  const handleDateSort = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Fotoğraf boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      alert('Lütfen sadece resim dosyası seçin');
      return;
    }

    // Preview için FileReader kullan
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setProfilePicturePreview(base64String);
      setProfilePictureBase64(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setProfilePicturePreview('');
    setProfilePictureBase64('');
  };

  const handleCheckPremiumExpiry = async () => {
    try {
      setCheckingPremium(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post(
        'https://api.sobcof.com/api/users/check-premium-expiry',
        { updateExpired: true }, // Süresi dolmuşları otomatik güncelle
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setPremiumCheckResults(response.data.data);
        setShowPremiumResultsDialog(true);

        // Kullanıcı listesini yenile
        await fetchUsers();
      } else {
        alert('Premium kontrolü başarısız oldu');
      }
    } catch (error) {
      console.error('Premium kontrol hatası:', error);
      alert('Premium kontrolü sırasında bir hata oluştu');
    } finally {
      setCheckingPremium(false);
    }
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      username: user.username || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      age: user.age || 0,
      gender: user.gender || '',
      bio: user.bio || '',
      location: user.location || '',
      isActive: user.isActive,
      isPremium: user.isPremium,
      isBanned: user.isBanned,
      banReason: user.banReason || '',
      // Coffee Beans & Game Stats
      coffeeBeansPoints: user.coffeeBeansPoints || 0,
      totalCoffeeBeansCollected: user.totalCoffeeBeansCollected || 0,
      beans: user.beans || 0,
      totalBeans: user.totalBeans || 0,
      hintCount: user.hintCount || 0
    });
    // Mevcut profil fotoğrafını göster
    setProfilePicturePreview(user.profilePicture || '');
    setProfilePictureBase64('');
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem('adminToken');

      // Fotoğraf varsa editForm'a ekle
      const dataToSend = {
        ...editForm,
        ...(profilePictureBase64 && { profilePictureBase64 })
      };

      if (selectedUser) {
        // Mevcut kullanıcıyı güncelle
        await axios.put(`https://api.sobcof.com/api/users/${selectedUser.id}`, dataToSend, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        alert('Kullanıcı başarıyla güncellendi!');
      } else {
        // Yeni kullanıcı oluştur
        const response = await axios.post(`https://api.sobcof.com/api/users`, dataToSend, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.data.success) {
          alert(`Kullanıcı başarıyla oluşturuldu!\n\nVarsayılan şifre: ${response.data.data.defaultPassword}\n\nBu şifreyi kullanıcıya güvenli bir şekilde iletin.`);
        }
      }

      setEditDialogOpen(false);
      setProfilePicturePreview('');
      setProfilePictureBase64('');
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      if (error.response?.data?.message) {
        alert('Hata: ' + error.response.data.message);
      } else {
        alert('Kullanıcı kaydedilemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBanUser = async (userId: string, isBanned: boolean) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.patch(`https://api.sobcof.com/api/users/${userId}/ban`, {
        isBanned,
        banReason: isBanned ? 'Admin tarafından yasaklandı' : ''
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchUsers();
    } catch (error) {
      console.error('Error banning user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        const token = localStorage.getItem('adminToken');
        await axios.delete(`https://api.sobcof.com/api/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        fetchUsers();
        alert('Kullanıcı başarıyla silindi');
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Kullanıcı silinirken hata oluştu');
      }
    }
  };

  const handleResetPassword = (user: User) => {
    setSelectedUser(user);
    setResetPasswordForm({
      sendEmail: true,
      newPassword: ''
    });
    setResetPasswordDialogOpen(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    try {
      setIsResettingPassword(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.post(`https://api.sobcof.com/api/users/${selectedUser.id}/reset-password`, {
        sendEmail: resetPasswordForm.sendEmail
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setResetPasswordDialogOpen(false);

        // E-posta gönderilmediyse şifreyi modal ile göster
        if (!resetPasswordForm.sendEmail && response.data.data.newPassword) {
          setGeneratedPassword(response.data.data.newPassword);
          setShowPasswordModal(true);
        } else {
          alert('Şifre başarıyla sıfırlandı ve kullanıcıya e-posta gönderildi!');
        }
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Şifre sıfırlama işlemi başarısız');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUserIds(users.map(u => u.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleBulkUpdateGender = async (gender: string) => {
    try {
      setIsBulkUpdating(true);
      const token = localStorage.getItem('adminToken');

      const response = await axios.put('https://api.sobcof.com/api/users/bulk-update', {
        userIds: selectedUserIds,
        updateData: { gender: gender }
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        alert(`${selectedUserIds.length} kullanıcının cinsiyeti başarıyla güncellendi!`);
        setSelectedUserIds([]);
        fetchUsers();
      }
    } catch (error: any) {
      console.error('Error in bulk update:', error);
      alert('Toplu güncelleme hatası: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsBulkUpdating(false);
      setBulkActionAnchorEl(null);
    }
  };

  const handleExportMetaAudience = async (gender: string) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`https://api.sobcof.com/api/users/export/meta-audience?gender=${gender}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `meta-audience-${gender}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting meta audience:', error);
      alert('Kullanıcı listesi dışa aktarılırken bir hata oluştu.');
    }
  };

  const handleOpenBulkMenu = (event: React.MouseEvent<HTMLElement>) => {
    setBulkActionAnchorEl(event.currentTarget);
  };

  const handleCloseBulkMenu = () => {
    setBulkActionAnchorEl(null);
  };

  const handleAutoPredictGender = () => {
    handleCloseBulkMenu();

    // Get selected user objects
    const selectedUsers = users.filter(user => selectedUserIds.includes(user.id));

    if (selectedUsers.length === 0) {
      alert('Lütfen önce listeden kullanıcı seçin.');
      return;
    }

    const results = selectedUsers.map(user => {
      // Use first name for prediction
      const predicted = GenderPredictor.predict(user.firstName);
      return {
        id: user.id,
        username: user.username,
        fullName: user.fullName || `${user.firstName} ${user.lastName}`,
        profilePicture: user.profilePicture, // Add profile picture
        currentGender: user.gender,
        predictedGender: predicted || user.gender || '', // Default to current or empty if null
        originalPrediction: predicted // To know if it was predicted or not
      };
    });

    setPredictionResults(results);
    setShowPredictionDialog(true);
  };

  const handleSavePredictions = async () => {
    try {
      setSavingPredictions(true);
      const token = localStorage.getItem('adminToken');

      // Group by new gender
      const malesToUpdate = predictionResults
        .filter(r => r.predictedGender === 'male' && r.predictedGender !== r.currentGender)
        .map(r => r.id);

      const femalesToUpdate = predictionResults
        .filter(r => r.predictedGender === 'female' && r.predictedGender !== r.currentGender)
        .map(r => r.id);

      let successMessage = '';
      let errorCount = 0;

      // Update Males
      if (malesToUpdate.length > 0) {
        try {
          await axios.put('https://api.sobcof.com/api/users/bulk-update', {
            userIds: malesToUpdate,
            updateData: { gender: 'male' }
          }, { headers: { 'Authorization': `Bearer ${token}` } });
          successMessage += `${malesToUpdate.length} kullanıcı 'Erkek' olarak güncellendi.\n`;
        } catch (err: any) {
          console.error('Error updating males:', err);
          console.error('Server Error Detail (Male):', err.response?.data);
          errorCount += malesToUpdate.length;
        }
      }

      // Update Females
      if (femalesToUpdate.length > 0) {
        try {
          await axios.put('https://api.sobcof.com/api/users/bulk-update', {
            userIds: femalesToUpdate,
            updateData: { gender: 'female' }
          }, { headers: { 'Authorization': `Bearer ${token}` } });
          successMessage += `${femalesToUpdate.length} kullanıcı 'Kadın' olarak güncellendi.\n`;
        } catch (err: any) {
          console.error('Error updating females:', err);
          console.error('Server Error Detail (Female):', err.response?.data);
          errorCount += femalesToUpdate.length;
        }
      }

      if (successMessage) {
        alert(successMessage + (errorCount > 0 ? `\n(Hata oluşan: ${errorCount})` : ''));
        setSelectedUserIds([]);
        setShowPredictionDialog(false);
        fetchUsers();
      } else if (errorCount > 0) {
        alert('Güncelleme sırasında hatalar oluştu.');
      } else {
        alert('Değişiklik yapılmadı (tahminler mevcut değerlerle aynı olabilir).');
        setShowPredictionDialog(false);
      }

    } catch (error: any) {
      console.error('Prediction save error:', error);
      console.error('Save Error Detail:', error.response?.data);
      alert('Kaydetme hatası oluştu. Konsola bakınız.');
    } finally {
      setSavingPredictions(false);
    }
  };

  const handlePredictionChange = (id: string, newGender: string) => {
    setPredictionResults(prev =>
      prev.map(item => item.id === id ? { ...item, predictedGender: newGender } : item)
    );
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
      {/* Enhanced App Bar */}
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            mr: 2,
            width: 40,
            height: 40
          }}>
            <Person sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{
            flexGrow: 1,
            fontWeight: 600,
            color: 'white'
          }}>
            Kullanıcı Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              // Yeni kullanıcı ekleme modalını aç
              setSelectedUser(null);
              setEditForm({
                firstName: '',
                lastName: '',
                username: '',
                email: '',
                phoneNumber: '',
                age: 0,
                gender: '',
                bio: '',
                location: '',
                isActive: true,
                isPremium: false,
                isBanned: false,
                banReason: '',
                // Coffee Beans & Game Stats
                coffeeBeansPoints: 0,
                totalCoffeeBeansCollected: 0,
                beans: 0,
                totalBeans: 0,
                hintCount: 0
              });
              setProfilePicturePreview('');
              setProfilePictureBase64('');
              setEditDialogOpen(true);
            }}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Yeni Kullanıcı
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Enhanced Filters */}
        <Card elevation={0} sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterList sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filtreler
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <TextField
                label="Kullanıcı Ara"
                variant="outlined"
                size="medium"
                fullWidth
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0); // Arama değiştiğinde sayfa numarasını sıfırla
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0); // Filtre değiştiğinde sayfa numarasını sıfırla
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="banned">Yasaklı</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Premium</InputLabel>
                <Select
                  value={premiumFilter}
                  label="Premium"
                  onChange={(e) => {
                    setPremiumFilter(e.target.value);
                    setPage(0); // Filtre değiştiğinde sayfa numarasını sıfırla
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="true">Premium</MenuItem>
                  <MenuItem value="false">Normal</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel>Cinsiyet</InputLabel>
                <Select
                  value={genderFilter}
                  label="Cinsiyet"
                  onChange={(e) => {
                    setGenderFilter(e.target.value);
                    setPage(0); // Filtre değiştiğinde sayfa numarasını sıfırla
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="male">Erkek</MenuItem>
                  <MenuItem value="female">Kadın</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={fetchUsers}
                fullWidth
                sx={{ borderRadius: 2, py: 1.5 }}
              >
                Yenile
              </Button>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Button
                variant="contained"
                startIcon={checkingPremium ? null : <CheckCircle />}
                onClick={handleCheckPremiumExpiry}
                disabled={checkingPremium}
                fullWidth
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #FFC700 0%, #FF9500 100%)',
                  }
                }}
              >
                {checkingPremium ? 'Kontrol Ediliyor...' : 'Premium Sürelerini Kontrol Et'}
              </Button>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Button
                variant="contained"
                onClick={() => handleExportMetaAudience('female')}
                fullWidth
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #1877f2 0%, #0a5dd8 100%)',
                  color: 'white',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(24, 119, 242, 0.2)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #166fe5 0%, #0950be 100%)',
                  }
                }}
              >
                Meta Kadın CSV İndir
              </Button>
            </Box>

            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <Button
                variant="outlined"
                onClick={() => handleExportMetaAudience('all')}
                fullWidth
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  borderColor: '#1877f2',
                  color: '#1877f2',
                  borderWidth: 1.5,
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#0a5dd8',
                    borderWidth: 1.5,
                    bgcolor: 'rgba(24, 119, 242, 0.04)',
                  }
                }}
              >
                Meta Tüm Kullanıcılar CSV
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalUsers}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Toplam Kullanıcı (Filtrelenmiş)
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #D2691E 0%, #CD853F 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {users.filter(u => u.isActive).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Aktif Kullanıcı (Bu Sayfa)
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #CD853F 0%, #DEB887 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {users.filter(u => u.isPremium).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Premium Kullanıcı (Bu Sayfa)
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #DEB887 0%, #F4A460 100%)',
              color: 'white'
            }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {users.filter(u => u.isBanned).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Yasaklı Kullanıcı (Bu Sayfa)
              </Typography>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{
              p: 2,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #F4A460 0%, #DEB887 100%)',
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <TrendingUp sx={{ mr: 1, fontSize: '1.5rem' }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {todayRegistrations}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Bugün Kayıt Olan
              </Typography>
            </Card>
          </Box>
        </Box>

        {/* Enhanced Users Table */}
        <Card elevation={0} sx={{
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < users.length}
                      checked={users.length > 0 && selectedUserIds.length === users.length}
                      onChange={handleSelectAllUsers}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Kullanıcı</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Yaş</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Cinsiyet</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Premium</TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      py: 2,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#e2e8f0' },
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                    onClick={handleDateSort}
                    title={`Kayıt tarihine göre ${sortOrder === 'desc' ? 'eskiden yeniye' : 'yeniden eskiye'} sırala`}
                  >
                    Kayıt Tarihi
                    <Sort
                      sx={{
                        fontSize: '1rem',
                        transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from(new Array(5)).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={150} /></TableCell>
                      <TableCell><Skeleton variant="text" width={50} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                      <TableCell><Skeleton variant="text" width={100} /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  users.map((user) => (
                    <TableRow
                      key={user.id}
                      hover
                      selected={selectedUserIds.includes(user.id)}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'background-color 0.2s'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell onClick={() => handleSelectUser(user.id)} sx={{ cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={user.profilePicture}
                            sx={{
                              bgcolor: user.isActive ? 'primary.main' : 'grey.400',
                              width: 40,
                              height: 40
                            }}
                          >
                            {user.profilePicture ? null : <Person />}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {user.fullName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              @{user.username}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.age}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            backgroundColor: '#e2e8f0',
                            color: '#475569'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.gender === 'male' ? 'Erkek' :
                              user.gender === 'female' ? 'Kadın' :
                                user.gender === 'other' ? 'Diğer' :
                                  'Belirtilmemiş'
                          }
                          size="small"
                          color={
                            user.gender === 'male' ? 'primary' :
                              user.gender === 'female' ? 'secondary' :
                                'default'
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Aktif' : 'Pasif'}
                          size="small"
                          color={user.isActive ? 'success' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isPremium ? 'Premium' : 'Normal'}
                          size="small"
                          color={user.isPremium ? 'warning' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditUser(user)}
                            sx={{
                              bgcolor: 'primary.50',
                              '&:hover': { bgcolor: 'primary.100' }
                            }}
                            title="Kullanıcıyı Düzenle"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleResetPassword(user)}
                            sx={{
                              bgcolor: 'info.50',
                              '&:hover': { bgcolor: 'info.100' }
                            }}
                            title="Şifreyi Sıfırla"
                          >
                            <LockReset fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color={user.isBanned ? 'success' : 'warning'}
                            onClick={() => handleBanUser(user.id, !user.isBanned)}
                            sx={{
                              bgcolor: user.isBanned ? 'success.50' : 'warning.50',
                              '&:hover': {
                                bgcolor: user.isBanned ? 'success.100' : 'warning.100'
                              }
                            }}
                            title={user.isBanned ? 'Yasağı Kaldır' : 'Kullanıcıyı Yasakla'}
                          >
                            {user.isBanned ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUser(user.id)}
                            sx={{
                              bgcolor: 'error.50',
                              '&:hover': {
                                bgcolor: 'error.100'
                              }
                            }}
                            title="Kullanıcıyı Sil"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalUsers}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            sx={{
              borderTop: '1px solid #e2e8f0',
              '& .MuiTablePagination-select': { borderRadius: 1 }
            }}
          />
        </Card>
      </Box>

      {/* Enhanced Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <Person />
          </Avatar>
          <Box>
            <Typography variant="h6">
              {selectedUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}
            </Typography>
            {selectedUser ? (
              <Typography variant="body2" color="text.secondary">
                {selectedUser.fullName} (@{selectedUser.username})
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Yeni kullanıcı bilgilerini girin
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Profil Fotoğrafı Bölümü */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Profil Fotoğrafı
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {profilePicturePreview ? (
                  <Box sx={{ position: 'relative' }}>
                    <Avatar
                      src={profilePicturePreview}
                      sx={{ width: 100, height: 100, border: '3px solid #8B4513' }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemovePhoto}
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Avatar sx={{ width: 100, height: 100, bgcolor: 'grey.300' }}>
                    <Person sx={{ fontSize: 50 }} />
                  </Avatar>
                )}
                <Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-picture-upload"
                    type="file"
                    onChange={handlePhotoChange}
                  />
                  <label htmlFor="profile-picture-upload">
                    <Button
                      variant="contained"
                      component="span"
                      sx={{
                        background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
                        mb: 1
                      }}
                    >
                      Fotoğraf Seç
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Maksimum 5MB, JPG/PNG formatında
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Ad"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                fullWidth
                variant="outlined"
                required
                error={!selectedUser && !editForm.firstName}
                helperText={!selectedUser && !editForm.firstName ? 'Ad gerekli' : ''}
              />
              <TextField
                label="Soyad"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                fullWidth
                variant="outlined"
                required
                error={!selectedUser && !editForm.lastName}
                helperText={!selectedUser && !editForm.lastName ? 'Soyad gerekli' : ''}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Kullanıcı Adı"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                fullWidth
                variant="outlined"
                required
                error={!selectedUser && !editForm.username}
                helperText={!selectedUser && !editForm.username ? 'Kullanıcı adı gerekli' : ''}
              />
              <TextField
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                fullWidth
                variant="outlined"
                required
                error={!selectedUser && !editForm.email}
                helperText={!selectedUser && !editForm.email ? 'Email gerekli' : ''}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Telefon"
                value={editForm.phoneNumber}
                onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                fullWidth
                variant="outlined"
              />
              <TextField
                label="Yaş"
                type="number"
                value={editForm.age}
                onChange={(e) => setEditForm({ ...editForm, age: parseInt(e.target.value) || 0 })}
                fullWidth
                variant="outlined"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Cinsiyet</InputLabel>
                <Select
                  value={editForm.gender}
                  label="Cinsiyet"
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                >
                  <MenuItem value="male">Erkek</MenuItem>
                  <MenuItem value="female">Kadın</MenuItem>
                  <MenuItem value="other">Diğer</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Konum"
                value={editForm.location}
                onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                fullWidth
                variant="outlined"
              />
            </Box>

            <TextField
              label="Biyografi"
              value={editForm.bio}
              onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Hesap Durumu
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label="Aktif"
                  color={editForm.isActive ? 'success' : 'default'}
                  onClick={() => setEditForm({ ...editForm, isActive: !editForm.isActive })}
                  clickable
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="Premium"
                  color={editForm.isPremium ? 'warning' : 'default'}
                  onClick={() => setEditForm({ ...editForm, isPremium: !editForm.isPremium })}
                  clickable
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label="Yasaklı"
                  color={editForm.isBanned ? 'error' : 'default'}
                  onClick={() => setEditForm({ ...editForm, isBanned: !editForm.isBanned })}
                  clickable
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            {editForm.isBanned && (
              <TextField
                label="Yasak Sebebi"
                value={editForm.banReason}
                onChange={(e) => setEditForm({ ...editForm, banReason: e.target.value })}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
              />
            )}

            {/* Coffee Beans & Game Stats Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#8B4513' }}>
                ☕ Coffee Beans & Oyun İstatistikleri
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Bugün Toplanan (coffeeBeansPoints)"
                  type="number"
                  value={editForm.coffeeBeansPoints}
                  onChange={(e) => setEditForm({ ...editForm, coffeeBeansPoints: parseInt(e.target.value) || 0 })}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">☕</InputAdornment>,
                  }}
                />
                <TextField
                  label="Toplam Toplanan Bean"
                  type="number"
                  value={editForm.totalCoffeeBeansCollected}
                  onChange={(e) => setEditForm({ ...editForm, totalCoffeeBeansCollected: parseInt(e.target.value) || 0 })}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">☕</InputAdornment>,
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Mevcut Bean Bakiyesi"
                  type="number"
                  value={editForm.beans}
                  onChange={(e) => setEditForm({ ...editForm, beans: parseInt(e.target.value) || 0 })}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">☕</InputAdornment>,
                  }}
                  helperText="Kullanıcının harcayabileceği bean"
                />
                <TextField
                  label="Toplam Kazanılan Bean"
                  type="number"
                  value={editForm.totalBeans}
                  onChange={(e) => setEditForm({ ...editForm, totalBeans: parseInt(e.target.value) || 0 })}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">☕</InputAdornment>,
                  }}
                />
                <TextField
                  label="İpucu Sayısı"
                  type="number"
                  value={editForm.hintCount}
                  onChange={(e) => setEditForm({ ...editForm, hintCount: parseInt(e.target.value) || 0 })}
                  variant="outlined"
                  sx={{ flex: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">💡</InputAdornment>,
                  }}
                  helperText="Oyunda kullanabileceği ipucu"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={uploadingPhoto || (!selectedUser && (!editForm.firstName || !editForm.lastName || !editForm.username || !editForm.email))}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)'
            }}
          >
            {uploadingPhoto ? 'Kaydediliyor...' : (selectedUser ? 'Güncelle' : 'Oluştur')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şifre Sıfırlama Dialog */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => setResetPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'info.main' }}>
            <LockReset />
          </Avatar>
          <Box>
            <Typography variant="h6">Şifre Sıfırla</Typography>
            {selectedUser && (
              <Typography variant="body2" color="text.secondary">
                {selectedUser.fullName} (@{selectedUser.username})
              </Typography>
            )}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{
              p: 2,
              bgcolor: 'warning.50',
              borderRadius: 2,
              border: '1px solid #ffeaa7'
            }}>
              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Dikkat
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bu işlem kullanıcının mevcut şifresini silecek ve yeni bir rastgele şifre oluşturacaktır.
                Kullanıcıya e-posta ile yeni şifre gönderilebilir.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Kullanıcıya e-posta gönder:
              </Typography>
              <Chip
                label={resetPasswordForm.sendEmail ? 'Evet' : 'Hayır'}
                color={resetPasswordForm.sendEmail ? 'success' : 'default'}
                onClick={() => setResetPasswordForm({
                  ...resetPasswordForm,
                  sendEmail: !resetPasswordForm.sendEmail
                })}
                clickable
                sx={{ fontWeight: 600 }}
              />
            </Box>

            {!resetPasswordForm.sendEmail && (
              <Box sx={{
                p: 2,
                bgcolor: 'info.50',
                borderRadius: 2,
                border: '1px solid #b3d9ff'
              }}>
                <Typography variant="body2" color="info.dark" sx={{ fontWeight: 600, mb: 1 }}>
                  ℹ️ Bilgi
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  E-posta gönderilmezse, yeni şifre bu sayfada gösterilecektir.
                  Lütfen şifreyi güvenli bir şekilde kullanıcıya iletin.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setResetPasswordDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
            disabled={isResettingPassword}
          >
            İptal
          </Button>
          <Button
            onClick={handleConfirmResetPassword}
            variant="contained"
            disabled={isResettingPassword}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
            }}
          >
            {isResettingPassword ? 'Sıfırlanıyor...' : 'Şifreyi Sıfırla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Şifre Gösterme Modal */}
      <Dialog
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{
          pb: 1,
          borderBottom: '1px solid #e2e8f0',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: 'success.main' }}>
            <CheckCircle />
          </Avatar>
          <Box>
            <Typography variant="h6">Şifre Başarıyla Sıfırlandı</Typography>
            <Typography variant="body2" color="text.secondary">
              Yeni şifre oluşturuldu
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{
              p: 2,
              bgcolor: 'success.50',
              borderRadius: 2,
              border: '1px solid #c8e6c9'
            }}>
              <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600, mb: 1 }}>
                ✅ İşlem Tamamlandı
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kullanıcının şifresi başarıyla sıfırlandı. Aşağıdaki yeni şifreyi kullanıcıya güvenli bir şekilde iletin.
              </Typography>
            </Box>

            <Box sx={{
              p: 3,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '2px solid #8B4513',
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Yeni Şifre:
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  color: '#8B4513',
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid #e0e0e0'
                }}
              >
                {generatedPassword}
              </Typography>
            </Box>

            <Box sx={{
              p: 2,
              bgcolor: 'warning.50',
              borderRadius: 2,
              border: '1px solid #ffeaa7'
            }}>
              <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 600, mb: 1 }}>
                ⚠️ Güvenlik Uyarısı
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Bu şifreyi güvenli bir şekilde kullanıcıya iletin<br />
                • Şifreyi kimseyle paylaşmayın<br />
                • Kullanıcının ilk girişinde şifresini değiştirmesini önerin
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => {
              setShowPasswordModal(false);
              setGeneratedPassword('');
            }}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
            }}
          >
            Tamam
          </Button>
        </DialogActions>
      </Dialog>

      {/* Premium Check Results Dialog */}
      <Dialog
        open={showPremiumResultsDialog}
        onClose={() => setShowPremiumResultsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', fontWeight: 600 }}>
          Premium Süre Kontrol Sonuçları
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {premiumCheckResults && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
                ✅ {premiumCheckResults.updated} kullanıcının premium'u kapatıldı
              </Typography>

              <Card sx={{ p: 2, bgcolor: '#e3f2fd', border: '1px solid #2196f3', mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
                  ℹ️ Önemli Bilgi
                </Typography>
                <Typography variant="body2" sx={{ color: '#424242' }}>
                  Eğer bu kullanıcılardan birinin <strong>gerçekten aktif Apple aboneliği</strong> varsa,
                  uygulamayı açtıklarında StoreKit otomatik olarak premium'u yeniden aktif edecek ve
                  Firebase'e kaydedecektir. Yanlışlıkla kapatma endişesi yok! 🔒
                </Typography>
              </Card>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  📊 Özet
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                  <Card sx={{ p: 2, bgcolor: '#fff3cd' }}>
                    <Typography variant="body2" color="text.secondary">Süresi Dolmuş</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {premiumCheckResults.expired.count}
                    </Typography>
                  </Card>
                  <Card sx={{ p: 2, bgcolor: '#d4edda' }}>
                    <Typography variant="body2" color="text.secondary">Hala Aktif</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {premiumCheckResults.active.count}
                    </Typography>
                  </Card>
                  <Card sx={{ p: 2, bgcolor: '#f8d7da' }}>
                    <Typography variant="body2" color="text.secondary">Bitiş Tarihi Yok</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {premiumCheckResults.noEndDate.count}
                    </Typography>
                  </Card>
                  <Card sx={{ p: 2, bgcolor: '#d1ecf1' }}>
                    <Typography variant="body2" color="text.secondary">Toplam Premium</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {premiumCheckResults.totalPremiumUsers}
                    </Typography>
                  </Card>
                </Box>

                {premiumCheckResults.expired.count > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      ⏰ Süresi Dolmuş Kullanıcılar (İlk 10)
                    </Typography>
                    <TableContainer component={Card}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kullanıcı Adı</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Bitiş Tarihi</TableCell>
                            <TableCell>Geçen Gün</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {premiumCheckResults.expired.users.slice(0, 10).map((user: any) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.username}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>
                                {new Date(user.expiryDate).toLocaleDateString('tr-TR')}
                              </TableCell>
                              <TableCell>{user.daysExpired} gün önce</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {premiumCheckResults.noEndDate.count > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                      ⚠️ Bitiş Tarihi Olmayan Premium Kullanıcılar
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Bu kullanıcılar manuel kontrol gerektirebilir
                    </Typography>
                    <TableContainer component={Card}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kullanıcı Adı</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Abonelik Tipi</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {premiumCheckResults.noEndDate.users.map((user: any) => (
                            <TableRow key={user.id}>
                              <TableCell>{user.username}</TableCell>
                              <TableCell>{user.email}</TableCell>
                              <TableCell>{user.currentSubscription}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPremiumResultsDialog(false)}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action FAB/Toolbar equivalent */}
      {selectedUserIds.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            bottom: 32,
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            borderRadius: '50px',
            backgroundColor: '#1e293b',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            zIndex: 1000,
            border: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {selectedUserIds.length} Kullanıcı Seçildi
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<Wc />}
              onClick={handleOpenBulkMenu}
              disabled={isBulkUpdating}
              sx={{
                borderRadius: '25px',
                bgcolor: '#8B4513',
                '&:hover': { bgcolor: '#A0522D' }
              }}
            >
              Cinsiyet Düzelt
            </Button>

            <Button
              variant="outlined"
              onClick={() => setSelectedUserIds([])}
              sx={{
                borderRadius: '25px',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.3)',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              İptal
            </Button>
          </Box>
        </Paper>
      )}


      {/* Bulk Action Menu */}
      <Menu
        anchorEl={bulkActionAnchorEl}
        open={Boolean(bulkActionAnchorEl)}
        onClose={handleCloseBulkMenu}
      >
        <MenuItem onClick={() => { handleCloseBulkMenu(); handleBulkUpdateGender('male'); }}>
          Erkek Olarak İşaretle
        </MenuItem>
        <MenuItem onClick={() => { handleCloseBulkMenu(); handleBulkUpdateGender('female'); }}>
          Kadın Olarak İşaretle
        </MenuItem>
        <MenuItem onClick={handleAutoPredictGender} sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          ✨ Otomatik Tahmin Et (İsimden)
        </MenuItem>
      </Menu>

      {/* Prediction Review Dialog */}
      <Dialog
        open={showPredictionDialog}
        onClose={() => setShowPredictionDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 'bold'
        }}>
          Cinsiyet Tahmin Önizleme
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Mevcut</TableCell>
                  <TableCell>Tahmin Edilen</TableCell>
                  <TableCell>Onay</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {predictionResults.map((result) => {
                  const isChanged = result.predictedGender !== result.currentGender;

                  return (
                    <TableRow key={result.id} hover sx={{ backgroundColor: isChanged ? '#f0fdf4' : 'inherit' }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar
                            src={result.profilePicture}
                            alt={result.username}
                            sx={{ width: 40, height: 40, mr: 2 }}
                          >
                            {result.fullName ? result.fullName.substring(0, 1).toUpperCase() : <Person />}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">{result.fullName}</Typography>
                            <Typography variant="caption" color="text.secondary">@{result.username}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {result.currentGender === 'male' ? 'Erkek' : result.currentGender === 'female' ? 'Kadın' : '-'}
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={result.predictedGender}
                          onChange={(e) => handlePredictionChange(result.id, e.target.value)}
                          sx={{
                            minWidth: 120,
                            backgroundColor: result.predictedGender === 'male' ? '#e0f2fe' : result.predictedGender === 'female' ? '#fce7f3' : 'white'
                          }}
                        >
                          <MenuItem value="">Seçiniz</MenuItem>
                          <MenuItem value="male">Erkek</MenuItem>
                          <MenuItem value="female">Kadın</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {result.predictedGender && (
                          <Chip
                            size="small"
                            label={result.predictedGender === 'male' ? 'Erkek' : 'Kadın'}
                            color={result.predictedGender === 'male' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
            Toplam {predictionResults.length} kullanıcı inceleniyor.
          </Typography>
          <Box>
            <Button onClick={() => setShowPredictionDialog(false)} color="inherit">
              İptal
            </Button>
            <Button
              onClick={handleSavePredictions}
              variant="contained"
              disabled={savingPredictions}
              sx={{ ml: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {savingPredictions ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
